import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { GeolocationCoordinates, GeolocationPositionError } from '@features/stores-overview/model/geolocation';
import { GeoLocationService } from '@features/stores-overview/service/geolocation.service';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { StoreViewModel } from '@models/api.models';
import { UmbracoImage } from '@models/umbraco.model';
import { easeInOutAnimation, foldOutTransition } from '@shared/animations/animations';
import { PotientialCtaPayload } from '@features/map/map.models';
import { Router } from '@angular/router';
import { GeolocationCodeError } from '@features/stores-overview/constants/error-codes.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActiveRegionPipe } from '../../pipes/active-region.pipe';
import { ByRegionPipe } from '../../pipes/by-region.pipe';
import { ToHtmlPopupPropPipe } from '../../pipes/to-html-popup-prop.pipe';
import { ToGeoJsonPipe } from '../../../map/to-geo-json.pipe';
import { TranslateSafePipe } from '../../../../@shared/translate-safe/translate-safe.pipe';
import { LoadingDotsComponent } from '../../../../@shared/loading-dots/loading-dots.component';
import { MapboxComponent } from '../../../map/mapbox/mapbox.component';
import { StoreItemComponent } from '../store-item/store-item.component';
import { FilterButtonComponent } from '../../../../@shared/form-elements/filter-button/filter-button.component';
import { NgClass, NgIf, NgFor, AsyncPipe, SlicePipe } from '@angular/common';
import { SvgIconComponent } from '../../../../@shared/svg-icon/svg-icon.component';

@UntilDestroy()
@Component({
    selector: 'app-stores',
    templateUrl: './stores.template.html',
    styleUrls: ['./stores.component.scss'],
    animations: [foldOutTransition, easeInOutAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SvgIconComponent, NgClass, NgIf, NgFor, FilterButtonComponent, StoreItemComponent, MapboxComponent, LoadingDotsComponent, AsyncPipe, SlicePipe, TranslateSafePipe, ToGeoJsonPipe, ToHtmlPopupPropPipe, ByRegionPipe, ActiveRegionPipe]
})
export class StoresComponent implements OnInit {
    public activeGeo = false;

    public LocationCodeError = GeolocationCodeError;

    public activeRegions: string[] = [];
    public storeListAnimationState: 'open' | 'closed' = 'closed';
    public storesImage?: UmbracoImage;

    public currentPosition?: GeolocationCoordinates;
    public currentPositionError: GeolocationPositionError;

    public defaultVisibleItems: number = 4;
    public visibleItems?: number | undefined = this.defaultVisibleItems;

    @Input() storesImages?: UmbracoImage[];

    constructor(
        private readonly cd: ChangeDetectorRef,
        public readonly storesService: StoresService,
        private readonly geolocationService: GeoLocationService,
        private readonly router: Router
    ) {}
    ngOnInit(): void {
        if (this.storesImages) {
            const [first] = this.storesImages;
            this.storesImage = first;
        }

        // Reset searchValue when initializing
        this.storesService.resetFilter();

        // Do a full search
        this.storesService.searchStoresBy({});

        this.geolocationService.currentPosition$.pipe(untilDestroyed(this)).subscribe(
            ({ coords: { latitude, longitude } }) => {
                if (!latitude || !longitude) {
                    return;
                }

                this.storesService.searchStoresBy({ latitude, longitude });
                this.activeGeo = true;
                this.cd.markForCheck();
            },
            (error: GeolocationPositionError) => {
                this.storesService.searchStoresBy({});
                this.currentPositionError = error;
                this.activeGeo = false;
                this.cd.markForCheck();
            }
        );

        // Whenever the searchParams updates the search
        // the regions should reset, since this is only a local filtering
        this.storesService.stores$.pipe(untilDestroyed(this)).subscribe(() => {
            this.activeRegions = [];
        });
    }

    public onSearchChange(searchValue: string): void {
        this.disableLocationService();
        this.storesService.searchStoresBy(searchValue ? { address: searchValue } : {});
    }

    public onActivateRegion(region: string): void {
        this.disableLocationService();

        this.activeRegions = this.activeRegions.includes(region)
            ? this.activeRegions.filter(i => i !== region)
            : [...this.activeRegions, region];
    }

    public seeMoreStores(): void {
        if (this.storeListAnimationState === 'closed') {
            this.visibleItems = undefined;
            this.storeListAnimationState = 'open';
            return;
        }

        this.storeListAnimationState = 'closed';
        this.visibleItems = this.defaultVisibleItems;
    }

    public trackByStore(index: number, store: StoreViewModel): string | number {
        return store?.storeId || index;
    }

    public disableLocationService(fetchStores?: boolean): void {
        this.activeGeo = false;
        this.currentPosition = undefined;
        if (fetchStores) {
            this.storesService.searchStoresBy({});
        }
    }

    public allowLocationAccess(): void {
        this.geolocationService.activateGeoLocation();
    }

    public onCtaClick({ event, feature }: PotientialCtaPayload): void {
        event.preventDefault();
        const url = feature?.properties?.storeUrl;
        if (url) {
            this.router.navigateByUrl(url);
        }
    }
}
